import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ShieldCheck, Eraser } from 'lucide-react';
import {
  getVerificationSubmission,
  verifyDocument,
  type DocumentBlock,
  type DocumentResponse,
  type DocumentSignatureRow,
  type DocumentSubmission,
} from '../../lib/documents';

interface DocumentSignOffModalProps {
  submissionId: string;
  onClose: () => void;
  onSigned: () => void;
}

/** A read-only summary of a completed submission + a verifier signature to finalize it. */
export default function DocumentSignOffModal({ submissionId, onClose, onSigned }: DocumentSignOffModalProps) {
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<DocumentSubmission | null>(null);
  const [responses, setResponses] = useState<DocumentResponse[]>([]);
  const [signatures, setSignatures] = useState<DocumentSignatureRow[]>([]);
  const [note, setNote] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getVerificationSubmission(submissionId);
        if (!active) return;
        if (!data) { setError('You are not authorized to sign off this document, or it is no longer pending.'); return; }
        setSubmission(data.submission);
        setResponses(data.responses);
        setSignatures(data.signatures);
      } catch (e: any) {
        if (active) setError(e?.message || 'Failed to load document.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submissionId]);

  const handleSignOff = async () => {
    setError(null);
    if (!signatureData) { setError('Please add your signature to sign off.'); return; }
    setSaving(true);
    try {
      await verifyDocument({ submissionId, signatureData, note: note.trim() || undefined });
      onSigned();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to sign off.');
    } finally {
      setSaving(false);
    }
  };

  const blocks = (submission?.template_snapshot || []) as DocumentBlock[];
  const respByBlock = new Map(responses.map((r) => [r.block_id, r]));
  const sigsByBlock = new Map<string, DocumentSignatureRow[]>();
  for (const s of signatures) {
    if (s.is_verification) continue; // not part of the original doc body
    const arr = sigsByBlock.get(s.block_id) || [];
    arr.push(s);
    sigsByBlock.set(s.block_id, arr);
  }

  const content = (
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-center justify-center p-4" onClick={saving ? undefined : onClose}>
      <div className="bg-gray-100 rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">Verification sign-off</div>
              <div className="text-[15px] font-semibold text-gray-900 truncate">{submission?.title || 'Document'}</div>
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              {submission && (
                <div className="bg-white rounded-xl border border-gray-200 p-3 text-[12px] text-gray-600">
                  Completed by <span className="font-semibold text-gray-900">{submission.submitted_by_name || 'Unknown'}</span>
                  {submission.submitted_at ? <> on {new Date(submission.submitted_at).toLocaleString()}</> : null}
                </div>
              )}

              {/* Read-only completed document */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                {blocks.map((b) => {
                  const cfg = b.config || {};
                  const label: string = cfg.label || cfg.text || '';
                  if (b.type === 'heading') return <div key={b.id} className="text-[15px] font-bold text-gray-900">{cfg.text}</div>;
                  if (b.type === 'rich_text') return <div key={b.id} className="text-[13px] text-gray-600 whitespace-pre-wrap">{cfg.text}</div>;
                  if (b.type === 'divider') return <hr key={b.id} className="border-gray-200" />;
                  if (b.type === 'image') return cfg.url ? <img key={b.id} src={cfg.url} alt={cfg.caption || ''} className="max-h-40 rounded-lg" /> : null;
                  const sigs = sigsByBlock.get(b.id) || [];
                  if (b.type === 'signature' || b.type === 'additional_signatures') {
                    return (
                      <div key={b.id}>
                        <div className="text-[12px] font-semibold text-gray-700 mb-1">{label || 'Signature'}</div>
                        <div className="flex flex-wrap gap-3">
                          {sigs.length === 0 ? <span className="text-[12px] text-gray-400">No signature</span> : sigs.map((s) => (
                            <div key={s.id} className="border border-gray-200 rounded-lg p-1.5 bg-gray-50">
                              {s.signature_data ? <img src={s.signature_data} alt="signature" className="h-12" /> : null}
                              {s.signer_name ? <div className="text-[11px] text-gray-500 mt-0.5">{s.signer_name}</div> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  const resp = respByBlock.get(b.id);
                  const val = resp?.value;
                  return (
                    <div key={b.id}>
                      <div className="text-[12px] font-semibold text-gray-700">{label}</div>
                      <div className="text-[13px] text-gray-900">{formatValue(b.type, val)}</div>
                    </div>
                  );
                })}
              </div>

              {/* Verifier sign-off */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="text-[13px] font-semibold text-gray-800">Your sign-off</div>
                <SignaturePad onChange={setSignatureData} />
                <div>
                  <label className="block text-[12px] font-medium text-gray-600 mb-1">Note (optional)</label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full text-[13px] border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Add a note with your sign-off…" />
                </div>
              </div>

              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white rounded-b-2xl flex justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSignOff} disabled={saving || loading || !!error && !submission} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing…</> : <><ShieldCheck className="h-4 w-4" /> Sign off & complete</>}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function formatValue(type: string, value: any): string {
  if (value == null || value === '') return '—';
  if (type === 'checklist' && typeof value === 'object') {
    const checked = Object.entries(value).filter(([, v]) => v).map(([k]) => k);
    return checked.length ? checked.join(', ') : '—';
  }
  if (type === 'acknowledge') return value ? 'Acknowledged' : '—';
  if (type === 'multiple_choice') return Array.isArray(value) ? value.join(', ') : String(value);
  if (type === 'date' && value?.stampedAt) return new Date(value.stampedAt).toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Lightweight canvas signature pad → PNG data URL. */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const rect = c.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * c.width, y: ((e.clientY - rect.top) / rect.height) * c.height };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = pos(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current && canvasRef.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvasRef.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange(null);
  };

  return (
    <div>
      <div className="relative border border-gray-300 rounded-lg bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={600}
          height={160}
          className="w-full touch-none"
          style={{ height: 140 }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        <button type="button" onClick={clear} className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-1.5 py-0.5 hover:bg-gray-100">
          <Eraser className="h-3 w-3" /> Clear
        </button>
      </div>
      <p className="text-[11px] text-gray-400 mt-1">Sign above to confirm your sign-off.</p>
    </div>
  );
}
